import { memo, type CSSProperties } from 'react'
import { broadcastClockLabel } from '../../lib/broadcast-day.js'
import {
  ambiguousSeasonAtOffset, CLOCK_SEASON_CLASS, clockChangeHourTag, clockChangeOf,
} from '../../lib/clock-change.js'
import { TIME_COL_WIDTH, timelineHours, timelineSteps } from './useAgendaLayout.js'

/** Heures réellement dessinées : la journée, ou plus si la nuit la prolonge. */
function shownHours(totalHeight: number, pxPerHour: number, date: string): number {
  return pxPerHour > 0 ? Math.max(timelineHours(date), totalHeight / pxPerHour) : timelineHours(date)
}

interface AgendaTimelineProps {
  pxPerHour: number
  nowLineY: number | null
  totalHeight: number
  /** Jour affiche : c'est lui qui donne la duree reelle de la graduation. */
  date: string
  /** Minuit d'antenne quand la nuit prolonge la journee : la colonne le nomme. */
  nextDayY?: number | null
}

export const AgendaTimeline = memo(function AgendaTimeline({ pxPerHour, nowLineY, totalHeight, date, nextDayY = null }: AgendaTimelineProps) {
  const pxPerMin = pxPerHour / 60
  const { labelEvery, tickEvery } = timelineSteps(pxPerHour)
  // La hauteur porte la queue de nuit quand la journée se prolonge après minuit.
  const tickCount = Math.floor((shownHours(totalHeight, pxPerHour, date) * 60) / tickEvery)
  // Le 25 octobre, les deux « 02:00 » se distinguent par leur saison ; un jour
  // ordinaire, `change` est nul et la colonne reste celle de toujours.
  const change = clockChangeOf(date)

  return (
    <div
      className={`agenda-timeline${change ? ' has-clock-change' : ''}`}
      style={{ '--timeline-width': `${TIME_COL_WIDTH}px`, '--timeline-height': `${totalHeight}px` } as CSSProperties}
      aria-hidden="true"
    >
      {change?.firstPass && change.secondPass && [
        { range: change.firstPass, season: change.before.season },
        { range: change.secondPass, season: change.after.season },
      ].map(({ range, season }) => (
        <div
          key={season}
          className={`agenda-timeline__clock-band ${CLOCK_SEASON_CLASS[season]}`}
          style={{
            '--timeline-y': `${Math.round(range.fromHours * pxPerHour)}px`,
            '--clock-band-height': `${Math.round((range.toHours - range.fromHours) * pxPerHour)}px`,
          } as CSSProperties}
        />
      ))}

      {Array.from({ length: tickCount + 1 }, (_, index) => {
        const minuteOffset = index * tickEvery
        const y = minuteOffset * pxPerMin
        const totalMin = Math.round(minuteOffset)
        const isLabel = Number.isInteger(minuteOffset) && totalMin % labelEvery === 0
        const position = { '--timeline-y': `${y}px` } as CSSProperties

        if (isLabel) {
          // L'etiquette est l'heure d'horloge reellement diffusee a cette
          // position. Deduire l'heure du seul rang de la graduation faisait
          // apparaitre une heure jamais diffusee au passage a l'heure d'ete.
          const clock = broadcastClockLabel(date, minuteOffset / 60)
          const isHour = clock.endsWith(':00')
          const season = ambiguousSeasonAtOffset(change, minuteOffset / 60)
          const tag = isHour ? clockChangeHourTag(change, minuteOffset / 60) : null
          const seasonClass = season ? ` ${CLOCK_SEASON_CLASS[season]}` : ''
          return (
            <div key={`label-${index}`} className={`agenda-timeline__label${isHour ? ' is-hour' : ''}${seasonClass}`} style={position}>
              {clock}
              {tag && (
                <>
                  <span className="agenda-timeline__season">{tag.season} · {tag.utc}</span>
                  <span className="agenda-timeline__pass">{tag.pass}</span>
                </>
              )}
            </div>
          )
        }

        return <div key={`tick-${index}`} className="agenda-timeline__tick" style={position} />
      })}

      {nextDayY !== null && nextDayY < totalHeight && (
        <div className="agenda-timeline__next-day" style={{ '--timeline-y': `${nextDayY}px` } as CSSProperties}>
          Lendemain
        </div>
      )}

      {nowLineY !== null && <div className="agenda-timeline__now" style={{ '--timeline-y': `${nowLineY}px` } as CSSProperties} />}
    </div>
  )
})

export const AgendaGridLines = memo(function AgendaGridLines({ pxPerHour, totalHeight, date }: { pxPerHour: number; totalHeight: number; date: string }) {
  const pxPerMin = pxPerHour / 60
  const minorStepMin = timelineSteps(pxPerHour).minorEvery
  const lineCount = Math.floor((shownHours(totalHeight, pxPerHour, date) * 60) / minorStepMin)

  return (
    <div className="agenda-grid-lines" aria-hidden="true">
      {Array.from({ length: lineCount + 1 }, (_, index) => {
        const minOffset = index * minorStepMin
        const y = minOffset * pxPerMin
        const isHour = minOffset % 60 === 0
        return (
          <div
            key={index}
            className={`agenda-grid-line${isHour ? ' is-hour' : ''}`}
            style={{ '--timeline-y': `${y}px` } as CSSProperties}
          />
        )
      })}
    </div>
  )
})

/**
 * Bande du lendemain, sous minuit d'antenne : la nuit jusqu'au premier élément
 * du jour suivant. Elle dit seulement où commence l'autre journée (la colonne
 * des heures la nomme) ; les blocs qui s'y trouvent restent manipulables.
 */
export const NextDayBand = memo(function NextDayBand({ top, height }: { top: number; height: number }) {
  if (height <= 0) return null
  return (
    <div
      className="agenda-next-day"
      style={{ '--timeline-y': `${top}px`, '--next-day-height': `${height}px` } as CSSProperties}
      aria-hidden="true"
    />
  )
})

export const NowLine = memo(function NowLine({ y, label }: { y: number; label: string }) {
  return (
    <div className="agenda-now-line" style={{ '--timeline-y': `${y}px` } as CSSProperties} aria-hidden="true">
      <div className="agenda-now-line__label">{label} MAINTENANT</div>
    </div>
  )
})
